import { useSyncExternalStore } from 'react';

/**
 * The current user's date/time display formats.
 *
 * Legacy pages read them from the layout seed (`window.serverData.userSetting`).
 * A page served only from the Symfony API calls `enableApiUserFormats()` (or
 * `loadApiUserFormats()`) in its entrypoint; after that the seed is never read and the
 * formats come from `GET /api/v1/user/settings` → `userSetting`. Until they arrive, or
 * when that request fails, every format is '' so callers fall back to their own default.
 */

export const USER_FORMAT_KEYS = ['twigDateFormat', 'twigTimeFormat', 'angularDateFormat', 'angularTimeFormat'];

let apiMode = false;
let apiFormats = null;
let version = 0;
const listeners = new Set();

function notify() {
	version += 1;
	listeners.forEach(listener => listener());
}

/**
 * Accepts the `/user/settings` payload (formats nested under `userSetting`) or a flat
 * formats object. Returns the four keys as strings, or null when there are no formats.
 */
export function normalizeUserFormats(payload) {
	if (!payload || typeof payload !== 'object') return null;
	const source = 'userSetting' in payload ? payload.userSetting : payload;
	if (!source || typeof source !== 'object') return null;
	return USER_FORMAT_KEYS.reduce((acc, key) => {
		acc[key] = typeof source[key] === 'string' ? source[key] : '';
		return acc;
	}, {});
}

export function enableApiUserFormats() {
	if (apiMode) return;
	apiMode = true;
	notify();
}

export function setApiUserFormats(payload) {
	apiMode = true;
	apiFormats = normalizeUserFormats(payload);
	notify();
}

/**
 * Switches to API mode and fetches the formats once. Never rejects: a failed request
 * leaves the defaults in place.
 *
 * @param {() => Promise<*>} fetchSettings  e.g. `userApi.getUserSettings`
 * @param {(response: *) => *} [unwrapResponse]  strips the API envelope
 */
export function loadApiUserFormats(fetchSettings, unwrapResponse = response => response) {
	enableApiUserFormats();
	return Promise.resolve()
		.then(fetchSettings)
		.then(response => setApiUserFormats(unwrapResponse(response)))
		.catch(() => {});
}

/** One format by key; '' when unknown so `getUserFormat(key) || DEFAULT` works. */
export function getUserFormat(key) {
	if (apiMode) return (apiFormats && apiFormats[key]) || '';
	const seed = typeof window !== 'undefined' ? window.serverData?.userSetting : null;
	return seed?.[key] || '';
}

/** Explicit formats (static renders) win over the shared state. */
export function resolveUserFormat(key, formats) {
	return formats ? formats[key] || '' : getUserFormat(key);
}

export function currentUserFormats() {
	return USER_FORMAT_KEYS.reduce((acc, key) => {
		acc[key] = getUserFormat(key);
		return acc;
	}, {});
}

export function subscribeUserFormats(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function getUserFormatsVersion() {
	return version;
}

/** Re-renders the caller when the formats change; use the result as a memo dependency. */
export function useUserFormatsVersion() {
	return useSyncExternalStore(subscribeUserFormats, getUserFormatsVersion, getUserFormatsVersion);
}
